import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ClipboardList } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import ProgressBar from './ProgressBar'
import { formatPlanDate } from '@/utils/date'
import { matchingService } from '@/services/matchingService'
import { planService } from '@/services/planService'
import { Plan } from '@/types/plan'

interface SummaryRow {
  plan: Plan
  menteeName: string
}

const MenteePlansSummary: React.FC = () => {
  const [rows, setRows] = useState<SummaryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [partialError, setPartialError] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const relationships = await matchingService.getMyRelationships()
        const active = (Array.isArray(relationships) ? relationships : []).filter(
          (r) => r.status === 'active'
        )

        const results = await Promise.allSettled(
          active.map(async (relationship) => {
            const plans = await planService.getPlansForRelationship(relationship.id)
            const menteeName = relationship.mentee
              ? `${relationship.mentee.firstName} ${relationship.mentee.lastName}`
              : 'Mentee'
            return (Array.isArray(plans) ? plans : [])
              .filter((plan) => plan.status === 'active')
              .map((plan) => ({ plan, menteeName }))
          })
        )

        if (cancelled) return

        const nextRows: SummaryRow[] = []
        let failed = false
        for (const result of results) {
          if (result.status === 'fulfilled') {
            nextRows.push(...result.value)
          } else {
            failed = true
          }
        }
        setRows(nextRows)
        setPartialError(failed)
      } catch {
        if (!cancelled) setPartialError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Mentee Plans</h2>
        </div>
      </CardHeader>
      <CardBody>
        {loading && (
          <div data-testid="plans-summary-skeleton" className="space-y-3 animate-pulse">
            {[0, 1].map((i) => (
              <div key={i} className="h-14 bg-gray-100 rounded-md" />
            ))}
          </div>
        )}

        {!loading && partialError && (
          <p className="mb-3 text-sm text-amber-600">
            Couldn't load plans for some mentees. Showing what we could fetch.
          </p>
        )}

        {!loading && rows.length === 0 && (
          <p className="text-gray-600 text-sm py-4 text-center">
            No active plans yet. Create a plan from a mentee's relationship page.
          </p>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map(({ plan, menteeName }) => (
              <Link
                key={plan.id}
                to={`/plans/${plan.id}`}
                className="block p-3 border rounded-md hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{menteeName}</span>
                    <span className="text-sm text-gray-500"> — </span>
                    <span className="text-sm text-gray-600">{plan.title}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>Ends {formatPlanDate(plan.endDate)}</span>
                  </div>
                </div>
                <ProgressBar value={plan.progressPercentage} label={`${menteeName} plan progress`} />
              </Link>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default MenteePlansSummary
